import React, {useState, useRef, useEffect} from "react";
import {NumberSegment} from "./NumberSegment";
import {ChevronUpIcon, ChevronDownIcon} from "@heroicons/react/20/solid";

interface NumberFieldProps {
    label?: string;
    value?: number | null;
    onChange?: (value: number | null) => void;
    minValue?: number;
    maxValue?: number;
    step?: number;
    isRequired?: boolean;
    isDisabled?: boolean;
    description?: string;
    placeholder?: string;
    showSteppers?: boolean;
    allowNegative?: boolean;
    decimalPlaces?: number;
    formatOptions?: Intl.NumberFormatOptions;
}

export function NumberField(props: NumberFieldProps) {
    const {
        label,
        value = null,
        onChange,
        minValue,
        maxValue,
        step = 1,
        isRequired = false,
        isDisabled = false,
        description,
        placeholder = '0',
        showSteppers = true,
        allowNegative = true,
        decimalPlaces = 0,
    } = props;

    // Split value into integer and decimal parts
    const [integerPart, setIntegerPart] = useState('');
    const [decimalPart, setDecimalPart] = useState('');
    const [focusedSegment, setFocusedSegment] = useState<'integer' | 'decimal' | null>(null);

    const integerRef = useRef<HTMLDivElement>(null);
    const decimalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (value !== null && value !== undefined) {
            if (decimalPlaces > 0) {
                const parts = value.toFixed(decimalPlaces).split('.');
                setIntegerPart(parts[0] || '');
                setDecimalPart(parts[1] || '');
            } else {
                setIntegerPart(Math.floor(value).toString());
                setDecimalPart('');
            }
        } else {
            setIntegerPart('');
            setDecimalPart('');
        }
    }, [value, decimalPlaces]);

    const assembleValue = (integer: string, decimal: string): number | null => {
        if (!integer && !decimal) return null;

        const intValue = integer ? parseInt(integer) : 0;
        const decValue = decimal ? parseInt(decimal) : 0;

        if (decimalPlaces > 0) {
            const decimalValue = decValue / Math.pow(10, decimal.length || decimalPlaces);
            return intValue + (intValue < 0 ? -decimalValue : decimalValue);
        }

        return intValue;
    };

    const handleIntegerChange = (newValue: string) => {
        setIntegerPart(newValue);
        const assembled = assembleValue(newValue, decimalPart);

        // Validate against min/max
        if (assembled !== null) {
            let finalValue = assembled;
            if (minValue !== undefined && assembled < minValue) {
                finalValue = minValue;
            }
            if (maxValue !== undefined && assembled > maxValue) {
                finalValue = maxValue;
            }
            onChange?.(finalValue);
        } else {
            onChange?.(null);
        }
    };

    const handleDecimalChange = (newValue: string) => {
        // Ensure decimal part doesn't exceed specified places
        const limitedValue = newValue.slice(0, decimalPlaces);
        setDecimalPart(limitedValue);
        const assembled = assembleValue(integerPart, limitedValue);
        onChange?.(assembled);
    };

    const handleIncrement = () => {
        const currentValue = value ?? 0;
        const newValue = currentValue + step;

        if (maxValue === undefined || newValue <= maxValue) {
            onChange?.(newValue);
        }
    };

    const handleDecrement = () => {
        const currentValue = value ?? 0;
        const newValue = currentValue - step;

        if (minValue === undefined || newValue >= minValue) {
            onChange?.(newValue);
        }
    };

    const canIncrement = value === null || maxValue === undefined || value < maxValue;
    const canDecrement = value === null || minValue === undefined || value > minValue;

    return (
        <div>
            {label && (
                <label className={`field-label ${isDisabled ? 'field-disabled' : ''}`}>
                    {label}
                    {isRequired && <span className={'field-required'}>*</span>}
                </label>
            )}

            <div className={`field-container ${isDisabled ? 'field-disabled' : ''}`}>
                <div className="flex items-center flex-1">
                    {/* Integer Part */}
                    <NumberSegment
                        value={integerPart}
                        onChange={handleIntegerChange}
                        placeholder={placeholder}
                        isDisabled={isDisabled}
                        minValue={minValue}
                        maxValue={maxValue}
                        allowNegative={allowNegative}
                        type="integer"
                        onFocus={() => setFocusedSegment('integer')}
                        onBlur={() => setFocusedSegment(null)}
                        onNext={() => {
                            if (decimalPlaces > 0) {
                                // Focus decimal part
                                const decimalInput = document.querySelector('[aria-label="Decimal part"]') as HTMLElement;
                                decimalInput?.focus();
                            }
                        }}
                        aria-label="Integer part"
                    />

                    {/* Decimal Separator and Part */}
                    {decimalPlaces > 0 && (
                        <>
                            <NumberSegment
                                value="."
                                onChange={() => {}}
                                isDisabled={isDisabled}
                                type="literal"
                            />

                            <NumberSegment
                                value={decimalPart}
                                onChange={handleDecimalChange}
                                placeholder={"0".repeat(decimalPlaces)}
                            isDisabled={isDisabled}
                            maxLength={decimalPlaces}
                            type="decimal"
                            onFocus={() => setFocusedSegment('decimal')}
                            onBlur={() => setFocusedSegment(null)}
                            onPrevious={() => {
                            // Focus integer part
                            const integerInput = document.querySelector('[aria-label="Integer part"]') as HTMLElement;
                            integerInput?.focus();
                        }}
                            aria-label="Decimal part"
                            />
                        </>
                    )}
                </div>

                {/* Steppers */}
                {showSteppers && !isDisabled && (
                    <div className="flex flex-col ml-2 -my-1">
                        <button
                            type="button"
                            onClick={handleIncrement}
                            disabled={!canIncrement}
                            className={`px-1 py-0.5 transition-colors rounded-t ${
                                canIncrement
                                    ? 'hover:bg-gray-100 active:bg-gray-200 text-gray-700'
                                    : 'text-gray-300 cursor-not-allowed'
                            }`}
                            aria-label="Increment"
                        >
                            <ChevronUpIcon className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={handleDecrement}
                            disabled={!canDecrement}
                            className={`px-1 py-0.5 transition-colors rounded-b ${
                                canDecrement
                                    ? 'hover:bg-gray-100 active:bg-gray-200 text-gray-700'
                                    : 'text-gray-300 cursor-not-allowed'
                            }`}
                            aria-label="Decrement"
                        >
                            <ChevronDownIcon className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>

            {description && (
                <div className={'mt-2 text-xs text-gray-600'}>
                    {description}
                </div>
            )}

            {isRequired && !value && (
                <div className={'field-error'}>
                    Value is required
                </div>
            )}
        </div>
    );
}